import React, { useState } from 'react'
import styles from './Panel.module.scss'

type PanelPadding = 'default' | 'compact' | 'none'
type PanelMargin = 'default' | 'none'
type HeaderVariant = 'default' | 'primary' | 'neutral' | 'secondary'
type HeaderSize = 'default' | 'sm'

interface PanelProps {
    children: React.ReactNode
    padding?: PanelPadding
    margin?: PanelMargin
    className?: string
    style?: React.CSSProperties
}

interface PanelHeaderProps {
    children: React.ReactNode
    className?: string
    /** Header style variant */
    variant?: HeaderVariant
    /** Header size — 'sm' reduces font size, padding, and gap */
    size?: HeaderSize
    /** Makes the panel collapsible when clicked */
    collapsible?: boolean
    /** Initial collapsed state (only used if collapsible is true) */
    defaultCollapsed?: boolean
    /** Controlled collapsed state */
    collapsed?: boolean
    /** Callback when collapse state changes */
    onCollapsedChange?: (collapsed: boolean) => void
    /** Optional content to show on the right side of the header */
    trailing?: React.ReactNode
}

type PanelBodyVariant = 'default' | 'no-padding'

interface PanelBodyProps {
    children: React.ReactNode
    className?: string
    /** Use 'no-padding' when body contains full-bleed content (e.g. list-table) that should go to the edges. */
    variant?: PanelBodyVariant
}

interface PanelFooterProps {
    children: React.ReactNode
    className?: string
}

const PanelRoot: React.FC<PanelProps> = ({
    children,
    padding = 'default',
    margin = 'default',
    className = '',
    style
}) => {
    const paddingClass = {
        'default': styles.paddingDefault,
        'compact': styles.paddingCompact,
        'none': styles.paddingNone,
    }[padding]

    const marginClass = {
        'default': styles.marginDefault,
        'none': styles.marginNone,
    }[margin]

    return (
        <div className={`${styles.panel} ${paddingClass} ${marginClass} ${className}`} style={style}>
            {children}
        </div>
    )
}

/**
 * Panel.Header - Styled header section
 * 
 * Variants control visual style (color/background):
 * - default: Simple header with bottom border
 * - primary: Green gradient for top-level sections
 * - secondary: Grey bar for nested/collapsible sections
 * - neutral: Gray gradient for reference sections
 * 
 * Sizes control dimensions (font size, padding, gap):
 * - default: Standard size
 * - sm: Compact size for nested/subordinate headers
 */
const PanelHeader: React.FC<PanelHeaderProps> = ({
    children,
    className = '',
    variant = 'default',
    size = 'default',
    collapsible = false,
    defaultCollapsed = false,
    collapsed: controlledCollapsed,
    onCollapsedChange,
    trailing
}) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed)

    // Use controlled state if provided, otherwise use internal state
    const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed

    const handleClick = () => {
        if (!collapsible) return

        const newState = !isCollapsed
        setInternalCollapsed(newState)
        onCollapsedChange?.(newState)
    }

    const variantClass = {
        'default': styles.panelHeader,
        'primary': styles.panelHeaderPrimary,
        'neutral': styles.panelHeaderNeutral,
        'secondary': styles.panelHeaderSecondary,
    }[variant]

    const sizeClass = size === 'sm' ? styles.headerSm : styles.headerDefault

    const headerClasses = [
        sizeClass,
        variantClass,
        collapsible ? styles.collapsible : '',
        className
    ].filter(Boolean).join(' ')

    // For collapsible headers, we need to expose the collapsed state
    // We do this by adding a data attribute that Panel.Body can read
    return (
        <div
            className={headerClasses}
            onClick={handleClick}
            data-collapsed={collapsible ? isCollapsed : undefined}
            role={collapsible ? 'button' : undefined}
            tabIndex={collapsible ? 0 : undefined}
            onKeyDown={collapsible ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleClick()
                }
            } : undefined}
        >
            {collapsible && (
                <span className={styles.collapseIcon}>
                    {isCollapsed ? '▶' : '▼'}
                </span>
            )}
            <div className={styles.headerContent}>
                {children}
            </div>
            {trailing && (
                <div className={styles.headerTrailing}>
                    {trailing}
                </div>
            )}
        </div>
    )
}

/**
 * Panel.Body - Content section that can be collapsed
 *
 * When used with a collapsible Panel.Header, manage collapsed state and
 * conditionally render the body yourself.
 *
 * variant "no-padding" removes body padding so content (e.g. list-table)
 * can be full-bleed to the panel edges.
 */
const PanelBody: React.FC<PanelBodyProps> = ({ children, className = '', variant = 'default' }) => {
    const variantClass = variant === 'no-padding' ? styles.panelBodyNoPadding : styles.panelBody
    return (
        <div className={`${variantClass} ${className}`}>
            {children}
        </div>
    )
}

/**
 * Panel.Footer - Full-bleed footer section that extends to panel edges
 * Useful for action sections, forms, or visually distinct bottom content
 */
const PanelFooter: React.FC<PanelFooterProps> = ({ children, className = '' }) => {
    return (
        <div className={`${styles.panelFooter} ${className}`}>
            {children}
        </div>
    )
}

export const Panel = Object.assign(PanelRoot, {
    Header: PanelHeader,
    Body: PanelBody,
    Footer: PanelFooter,
})

export type { HeaderVariant, HeaderSize, PanelPadding, PanelMargin }
